import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import {
  blockingAnomalyTypesCountAtom,
  toCheckAnomalyTypesCountAtom,
} from '@/states/anomaly-count.state';
import { useAtomValue } from 'jotai';
import { AlertCircle, AlertTriangle, ChevronRight } from 'lucide-react';

interface AlertButtonProps {
  onClick: () => void;
}

export const AlertButton: React.FC<AlertButtonProps> = ({ onClick }) => {
  const isLocked = useIsOfferLocked();

  const { t } = useTranslation();

  const isInternalUser = !!useUser()?.isInternalUser;
  const blockingCount = useAtomValue(blockingAnomalyTypesCountAtom);
  const toCheckCount = useAtomValue(toCheckAnomalyTypesCountAtom);

  const blocking = isInternalUser ? blockingCount : 0;
  const toCheck = isInternalUser ? toCheckCount : 0;

  if (blocking + toCheck === 0) {
    return null;
  }

  return (
    <div className="flex w-full items-center justify-center pt-4 shadow-sm">
      <Button
        variant="outline"
        size="lg"
        onClick={isLocked ? undefined : onClick}
        className={cn(
          'w-full rounded-md border p-4',
          blocking > 0
            ? 'border-red-200 bg-red-50 hover:bg-red-100'
            : 'border-orange-200 bg-orange-50 hover:bg-orange-100'
        )}
        disabled={isLocked}
      >
        <div className="flex w-full items-center gap-2 px-2 text-sm">
          <span className="font-semibold text-gray-700">
            {t('components.alert_button.detected_label')}
          </span>
          {blocking > 0 && (
            <span className="flex items-center gap-1.5">
              <AlertCircle className="size-4 text-red-500" />
              <span className="font-semibold text-red-500">
                {t('components.alert_button.detected_blocking', {
                  count: blocking,
                })}
              </span>
            </span>
          )}
          {blocking > 0 && toCheck > 0 && (
            <span className="text-gray-300">·</span>
          )}
          {toCheck > 0 && (
            <span className="flex items-center gap-1.5">
              <AlertTriangle className="size-4 text-orange-500" />
              <span className="font-semibold text-orange-500">
                {t('components.alert_button.detected_to_check', {
                  count: toCheck,
                })}
              </span>
            </span>
          )}
          <ChevronRight className="ml-auto size-4 text-gray-500" />
        </div>
      </Button>
    </div>
  );
};
